import { createTheme } from '@mui/material';
import { C, FONT, GLASS_BLUR, PANEL_RADIUS, glow, hexToRgba } from './tokens';

export const theme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: C.bg, paper: C.surface },
    primary: { main: C.wormhole, contrastText: C.textBright },
    secondary: { main: C.wormhole, contrastText: C.textBright },
    error: { main: C.red },
    info: { main: C.cyan },
    text: { primary: C.text, secondary: C.textDim },
  },
  typography: {
    fontFamily: FONT.body,
    h1: { fontFamily: FONT.display, fontWeight: 700 },
    h2: { fontFamily: FONT.display, fontWeight: 700 },
    h3: { fontFamily: FONT.sub, fontWeight: 700 },
    h4: { fontFamily: FONT.sub, fontWeight: 600 },
    h5: { fontFamily: FONT.sub, fontWeight: 600 },
    h6: { fontFamily: FONT.sub, fontWeight: 600 },
  },
  shape: { borderRadius: PANEL_RADIUS },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { scrollBehavior: 'smooth' },
        body: { backgroundColor: C.bg },
        '::selection': { backgroundColor: hexToRgba(C.wormhole, 0.4), color: C.textBright },
      },
    },
    // Hulls are translucent so the parallax void reads THROUGH the console
    // rather than being boxed out by it — that see-through is what makes the
    // backdrop feel like the space the panels are floating in. Transient
    // overlays (Menu/Popover/Autocomplete) opt back into an opaque fill
    // below: text over a moving starfield is not legible.
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: hexToRgba(C.surface, 0.72),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${C.hairline}`,
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: FONT.sub,
          fontWeight: 600,
          letterSpacing: '0.04em',
          borderRadius: 999,
          transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        },
        // A real button, not a status mark — this is the one place a full
        // pill and a solid fill are correct.
        contained: {
          borderRadius: 999,
          fontFamily: FONT.sub,
          fontWeight: 700,
          letterSpacing: 0.5,
          boxShadow: 'none',
        },
        containedPrimary: {
          boxShadow: glow(C.wormhole, 24, 0.45),
          '&:hover': { backgroundColor: C.wormhole, boxShadow: glow(C.wormhole, 40, 0.65) },
        },
        outlined: {
          borderColor: C.hairlineBright,
          '&:hover': { borderColor: C.wormhole, backgroundColor: hexToRgba(C.wormhole, 0.08), boxShadow: glow(C.wormhole, 16, 0.28) },
        },
        text: {
          '&:hover': { backgroundColor: hexToRgba(C.text, 0.06) },
        },
      },
    },
    // Fallback only — StatusBadge is the real status/tag component; this
    // exists so a Chip that slips through still reads border-only.
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontFamily: FONT.mono, backgroundColor: 'transparent', border: `1px solid currentColor` },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderRadius: 10,
          '& fieldset': { borderColor: C.hairline },
          '&:hover fieldset': { borderColor: C.hairlineBright },
          '&.Mui-focused fieldset': { borderColor: C.cyan, borderWidth: 1 },
        },
        input: { color: C.text },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: C.textDim, fontFamily: FONT.sub, '&.Mui-focused': { color: C.blue } } },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: C.panel,
          backdropFilter: 'none',
          border: `1px solid ${C.hairlineBright}`,
          boxShadow: '0 10px 34px rgba(5,4,12,0.55), 0 2px 8px rgba(5,4,12,0.4)',
        },
      },
    },
    // Menus/Popovers are transient overlays, not persistent panels — a real
    // offset shadow so a floating surface reads as detached, plus a blur
    // since these don't resize live the way Autocomplete's paper does.
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: hexToRgba(C.panel, 0.94),
          backdropFilter: GLASS_BLUR,
          border: `1px solid ${C.hairlineBright}`,
          boxShadow: '0 10px 34px rgba(5,4,12,0.55), 0 2px 8px rgba(5,4,12,0.4)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: hexToRgba(C.panel, 0.94),
          backdropFilter: GLASS_BLUR,
          border: `1px solid ${C.hairlineBright}`,
          boxShadow: '0 10px 34px rgba(5,4,12,0.55), 0 2px 8px rgba(5,4,12,0.4)',
        },
      },
    },
    // A real modal, not a persistent panel — opaque like Menu/Popover above
    // (DESIGN.md: a floating overlay is never translucent over the backdrop),
    // with the same wormhole-tinted hairline and offset shadow so it reads as
    // the same instrument family as every other floating surface in the app.
    // The backdrop itself is intentionally left untouched here rather than
    // via a global MuiBackdrop override — Menu/Popover/Autocomplete all set
    // their own backdrop `invisible: true`, and a theme-wide backdrop color
    // risks fighting that in ways that are annoying to verify are truly
    // inert; Dialog usages instead tint their own via `slotProps.backdrop`.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: hexToRgba(C.panel, 0.97),
          backdropFilter: GLASS_BLUR,
          border: `1px solid ${C.hairlineBright}`,
          boxShadow: `0 20px 60px rgba(5,4,12,0.6), ${glow(C.wormhole, 30, 0.14)}`,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: C.cyan, height: 2, borderRadius: 999, boxShadow: glow(C.cyan, 10, 0.6) },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontFamily: FONT.sub,
          fontWeight: 600,
          letterSpacing: '0.1em',
          color: C.textDim,
          transition: 'color 0.25s ease',
          '&.Mui-selected': { color: C.textBright },
        },
      },
    },
  },
});
